import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ArrowBack, Block, CheckCircle, PauseCircle, PlayCircle } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type { AdminTenantUser, OrganizationCommercialProduct, OrganizationSummary, OrganizationType } from '@/api/admin';
import { getProductCatalog, type ProductCatalogEntry } from '@/api/catalog';
import type { SubscriptionStatus, TenantUserStatus } from '@/api/types';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { StatusChip } from '@/components/StatusChip';
import { SubscriptionStatusChip } from '@/components/SubscriptionStatusChip';
import { PLAN_LABEL, SUBSCRIPTION_STATUS_LABEL } from '@/lib/labels';
import { formatDate, formatDateTime } from '@/lib/formatters';

const COMMERCIAL_OVERRIDE_STATUSES: Exclude<SubscriptionStatus, 'Cancelled'>[] = ['Trial', 'Active', 'PastDue'];

const USER_STATUS: Record<TenantUserStatus, { label: string; color: 'success' | 'default' | 'warning' }> = {
  Active: { label: 'Activo', color: 'success' },
  Invited: { label: 'Invitado', color: 'warning' },
  Disabled: { label: 'Deshabilitado', color: 'default' },
};

const ORG_TYPE: Record<OrganizationType, { label: string; color: 'default' | 'primary' | 'secondary' | 'info' }> = {
  None: { label: 'Sin productos', color: 'default' },
  Empresa: { label: 'Producto por clasificar', color: 'primary' },
  Estudio: { label: 'Estudio Contable', color: 'primary' },
  Integrador: { label: 'API para Programadores', color: 'info' },
  Hibrida: { label: 'Híbrida', color: 'secondary' },
};

export const Route = createFileRoute('/_authenticated/admin/tenants/$id')({
  component: AdminTenantDetailPage,
});

function AdminTenantDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tenantQ = useQuery({ queryKey: ['admin-tenant', id], queryFn: () => adminApi.getTenant(id) });
  const orgQ = useQuery({ queryKey: ['admin-organization', id], queryFn: () => adminApi.getOrganization(id) });
  const catalogQ = useQuery({ queryKey: ['catalog', 'products', 'admin'], queryFn: getProductCatalog, staleTime: 5 * 60_000 });
  const usersQ = useQuery({ queryKey: ['admin-tenant-users', id], queryFn: () => adminApi.listTenantUsers(id) });
  const couponQ = useQuery({ queryKey: ['admin-tenant-coupon', id], queryFn: () => adminApi.getTenantCoupon(id) });
  const couponsListQ = useQuery({ queryKey: ['admin-coupons'], queryFn: adminApi.listCoupons });

  const [error, setError] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(14);
  const [couponDraft, setCouponDraft] = useState('');
  const [commercialTierDraft, setCommercialTierDraft] = useState('');
  const [commercialStatusDraft, setCommercialStatusDraft] = useState<Exclude<SubscriptionStatus, 'Cancelled'>>('Active');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [disableTarget, setDisableTarget] = useState<AdminTenantUser | null>(null);
  const [disableReason, setDisableReason] = useState('');

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin-tenant', id] });
    void qc.invalidateQueries({ queryKey: ['admin-organization', id] });
    void qc.invalidateQueries({ queryKey: ['admin-tenant-users', id] });
    void qc.invalidateQueries({ queryKey: ['organizations'] });
  };
  const onError = (e: unknown): void => setError(toApiError(e).message);

  const suspendMut = useMutation({
    mutationFn: (reason: string) => adminApi.suspendTenant(id, reason || undefined),
    onSuccess: () => {
      setSuspendOpen(false);
      setSuspendReason('');
      invalidate();
    },
    onError,
  });
  const reactivateMut = useMutation({
    mutationFn: () => adminApi.reactivateTenant(id),
    onSuccess: invalidate,
    onError,
  });
  const extendMut = useMutation({
    mutationFn: (days: number) => adminApi.extendTenantBilling(id, days),
    onSuccess: invalidate,
    onError,
  });
  const commercialOverrideMut = useMutation({
    mutationFn: () => {
      const selected = catalogQ.data?.tiers.find((tier) => productTierKey(tier) === commercialTierDraft);
      if (!selected) throw new Error('Elegí un producto/tier comercial válido.');
      return adminApi.applyTenantCommercialProductOverride(id, selected.product, selected.tier, commercialStatusDraft);
    },
    onSuccess: () => {
      setCommercialTierDraft('');
      invalidate();
    },
    onError,
  });
  const disableMut = useMutation({
    mutationFn: (vars: { userId: string; reason: string }) =>
      adminApi.disableTenantUser(id, vars.userId, vars.reason || undefined),
    onSuccess: () => {
      setDisableTarget(null);
      setDisableReason('');
      invalidate();
    },
    onError,
  });
  const enableMut = useMutation({
    mutationFn: (userId: string) => adminApi.enableTenantUser(id, userId),
    onSuccess: invalidate,
    onError,
  });
  const applyCouponMut = useMutation({
    mutationFn: (couponCode: string) => adminApi.applyTenantCoupon(id, couponCode),
    onSuccess: () => {
      setCouponDraft('');
      void qc.invalidateQueries({ queryKey: ['admin-tenant-coupon', id] });
    },
    onError,
  });
  const removeCouponMut = useMutation({
    mutationFn: () => adminApi.removeTenantCoupon(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-tenant-coupon', id] }),
    onError,
  });

  if (tenantQ.isLoading) return <LoadingScreen message="Cargando tenant…" />;

  if (tenantQ.isError || !tenantQ.data) {
    return (
      <Box>
        <BackButton onClick={() => navigate({ to: '/admin/organizaciones' })} />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => tenantQ.refetch()}>
              Reintentar
            </Button>
          }
        >
          {tenantQ.error ? toApiError(tenantQ.error).message : 'Tenant no encontrado.'}
        </Alert>
      </Box>
    );
  }

  const t = tenantQ.data;
  const isActive = t.status === 'Active';
  const tenantBusy =
    suspendMut.isPending || reactivateMut.isPending || extendMut.isPending;
  const usersBusy = disableMut.isPending || enableMut.isPending;
  const users = usersQ.data?.items ?? [];
  const canApplyCouponDraft = (couponsListQ.data ?? []).some(
    (coupon) => coupon.code === couponDraft && isAutomaticallyApplicableCoupon(coupon),
  );

  return (
    <Box>
      <BackButton onClick={() => navigate({ to: '/admin/organizaciones' })} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4">{t.name}</Typography>
        <StatusChip kind="tenant" value={t.status} />
        <SubscriptionStatusChip value={t.subscriptionStatus} />
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* Acciones de cuenta */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Acciones de cuenta
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Las acciones comerciales viven en “Productos comerciales”. El dato histórico de facturación se
            conserva sólo para investigar cuentas migradas.
          </Alert>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            {isActive ? (
              <Button
                color="warning"
                variant="outlined"
                startIcon={<PauseCircle />}
                disabled={tenantBusy}
                onClick={() => setSuspendOpen(true)}
              >
                Suspender
              </Button>
            ) : (
              <Button
                color="success"
                variant="outlined"
                startIcon={<PlayCircle />}
                disabled={tenantBusy}
                onClick={() => reactivateMut.mutate()}
              >
                {reactivateMut.isPending ? <CircularProgress size={18} /> : 'Reactivar'}
              </Button>
            )}

            <Box sx={{ width: 1, height: 28, bgcolor: 'divider', mx: 0.5 }} />

            <Typography variant="body2" color="text.secondary">
              Vencimiento
            </Typography>
            <Select
              size="small"
              value={extendDays}
              onChange={(e) => setExtendDays(Number(e.target.value))}
              sx={{ minWidth: 110 }}
            >
              {[14, 30, 60, 90].map((d) => (
                <MenuItem key={d} value={d}>
                  +{d} días
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="outlined"
              size="small"
              disabled={tenantBusy}
              onClick={() => extendMut.mutate(extendDays)}
            >
              {extendMut.isPending ? <CircularProgress size={18} /> : 'Extender'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Datos del tenant */}
      <CommercialProductsCard
        organization={orgQ.data}
        loading={orgQ.isLoading}
        error={orgQ.error}
        coupon={couponQ.data ?? null}
        tiers={catalogQ.data?.tiers ?? []}
        selectedTier={commercialTierDraft}
        selectedStatus={commercialStatusDraft}
        isSubmitting={commercialOverrideMut.isPending}
        onTierChange={setCommercialTierDraft}
        onStatusChange={setCommercialStatusDraft}
        onSubmit={() => commercialOverrideMut.mutate()}
      />

      {/* Datos del tenant */}
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={3}>
            <Field label="ID" value={t.id} mono />
            <Field label="Email" value={t.email} />
            <Field label="Dato histórico de facturación" value={PLAN_LABEL[t.plan]} />
            <Field label="CUITs máximos" value={String(t.maxCuitProfiles)} />
            <Field label="Suscripción iniciada" value={formatDate(t.subscriptionStartedAt)} />
            <Field label="Próxima facturación" value={formatDate(t.nextBillingAt)} />
            <Field label="Creado" value={formatDateTime(t.createdAt)} />
            <Field label="Actualizado" value={formatDateTime(t.updatedAt)} />
          </Grid>
        </CardContent>
      </Card>

      {/* Bonificación aplicada al tenant */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Bonificación comercial
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Beneficio asignado a toda la cuenta, no a un producto o tier particular. Hoy sólo una
            bonificación del 100% evita crear el checkout y cobrar; mantiene los límites, el estado y
            la medición de cada producto contratado.
          </Typography>
          {couponQ.data ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                color={isAutomaticallyApplicableCoupon(couponQ.data) ? 'success' : 'warning'}
                label={commercialDiscountSummary(couponQ.data)}
              />
              <Typography variant="caption" color="text.secondary">
                aplicado {formatDate(couponQ.data.appliedAt)}
                {isAutomaticallyApplicableCoupon(couponQ.data) ? '' : ' · registrado sin efecto automático'}
              </Typography>
              <Button
                size="small"
                color="warning"
                disabled={removeCouponMut.isPending}
                onClick={() => removeCouponMut.mutate()}
              >
                Quitar
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Select
                size="small"
                displayEmpty
                value={couponDraft}
                onChange={(e) => setCouponDraft(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">Elegí un cupón…</MenuItem>
                {(couponsListQ.data ?? [])
                  .filter((c) => c.active)
                  .map((c) => (
                    <MenuItem key={c.code} value={c.code} disabled={!isAutomaticallyApplicableCoupon(c)}>
                      {c.code} · {c.percentOff}%{isAutomaticallyApplicableCoupon(c) ? '' : ' · sin efecto automático'}
                    </MenuItem>
                  ))}
              </Select>
              <Button
                variant="contained"
                size="small"
                disabled={!canApplyCouponDraft || applyCouponMut.isPending}
                onClick={() => couponDraft && applyCouponMut.mutate(couponDraft)}
              >
                {applyCouponMut.isPending ? <CircularProgress size={18} /> : 'Aplicar'}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Usuarios del tenant */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Usuarios {usersQ.data ? `(${usersQ.data.total})` : ''}
          </Typography>
          {usersQ.isLoading ? (
            <CircularProgress size={22} />
          ) : usersQ.isError ? (
            <Alert severity="error">{toApiError(usersQ.error).message}</Alert>
          ) : users.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin usuarios.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>
                        <Chip size="small" color={USER_STATUS[u.status].color} label={USER_STATUS[u.status].label} />
                      </TableCell>
                      <TableCell align="right">
                        {u.status === 'Disabled' ? (
                          <Button
                            size="small"
                            color="success"
                            startIcon={<CheckCircle />}
                            disabled={usersBusy}
                            onClick={() => enableMut.mutate(u.id)}
                          >
                            Reactivar
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="warning"
                            startIcon={<Block />}
                            disabled={usersBusy}
                            onClick={() => setDisableTarget(u)}
                          >
                            Deshabilitar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo: suspender tenant */}
      <Dialog open={suspendOpen} onClose={() => setSuspendOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Suspender “{t.name}”</DialogTitle>
        <DialogContent>
          <DialogContentText>
            El tenant no podrá emitir comprobantes hasta reactivarlo. Indicá un motivo (opcional, queda en
            auditoría).
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Motivo"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendOpen(false)}>Cancelar</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={suspendMut.isPending}
            onClick={() => suspendMut.mutate(suspendReason)}
          >
            {suspendMut.isPending ? <CircularProgress size={18} /> : 'Suspender'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: deshabilitar usuario */}
      <Dialog open={!!disableTarget} onClose={() => setDisableTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Deshabilitar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {disableTarget ? `${disableTarget.name} (${disableTarget.email}) ` : ''}
            no podrá iniciar sesión. Se puede revertir. Motivo (opcional):
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Motivo"
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableTarget(null)}>Cancelar</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={disableMut.isPending}
            onClick={() => disableTarget && disableMut.mutate({ userId: disableTarget.id, reason: disableReason })}
          >
            {disableMut.isPending ? <CircularProgress size={18} /> : 'Deshabilitar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function BackButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <Button startIcon={<ArrowBack />} onClick={onClick} sx={{ mb: 2 }} size="small">
      Volver a Organizaciones
    </Button>
  );
}

function CommercialProductsCard({
  organization,
  loading,
  error,
  coupon,
  tiers,
  selectedTier,
  selectedStatus,
  isSubmitting,
  onTierChange,
  onStatusChange,
  onSubmit,
}: {
  organization?: OrganizationSummary;
  loading: boolean;
  error: unknown;
  coupon: adminApi.CouponApplication | null;
  tiers: ProductCatalogEntry[];
  selectedTier: string;
  selectedStatus: Exclude<SubscriptionStatus, 'Cancelled'>;
  isSubmitting: boolean;
  onTierChange: (value: string) => void;
  onStatusChange: (value: Exclude<SubscriptionStatus, 'Cancelled'>) => void;
  onSubmit: () => void;
}): JSX.Element {
  const products = organization?.commercialProducts ?? [];
  const selectedCatalogTier = tiers.find((tier) => productTierKey(tier) === selectedTier);

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Productos comerciales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fuente vigente para soporte, onboarding, módulos visibles y cambios de producto.
            </Typography>
          </Box>
          {organization ? (
            <Chip
              size="small"
              color={ORG_TYPE[organization.organizationType].color}
              label={ORG_TYPE[organization.organizationType].label}
            />
          ) : null}
        </Stack>

        {loading ? (
          <CircularProgress size={22} />
        ) : error ? (
          <Alert severity="warning">
            No se pudo cargar la clasificación comercial. El dato histórico de facturación sigue disponible abajo.
          </Alert>
        ) : products.length > 0 ? (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {products.map((p) => (
                <CommercialProductChip key={`${p.technicalProduct}:${p.tier}`} product={p} />
              ))}
            </Stack>
            {coupon ? (
              <Alert severity="success" sx={{ py: 0.75 }}>
                {commercialDiscountSummary(coupon)} asignado a toda la cuenta. Se mantiene como
                bonificación y no altera el nombre ni el tier de sus productos.
              </Alert>
            ) : null}
          </Stack>
        ) : (
          <Alert severity="info">
            Esta organización todavía no tiene productos comerciales operativos. Migrala antes de
            aplicar ajustes comerciales.
          </Alert>
        )}

        <Box sx={{ mt: 2.5, pt: 2, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Ajuste comercial auditado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Uso interno para migración, prueba auditada o bonificación operativa. Para cancelaciones,
            usá el flujo con proveedor de pagos.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <Select
              size="small"
              displayEmpty
              value={selectedTier}
              onChange={(e) => onTierChange(e.target.value)}
              sx={{ minWidth: 300 }}
            >
              <MenuItem value="">Elegí producto y nivel…</MenuItem>
              {tiers.map((tier) => (
                <MenuItem key={productTierKey(tier)} value={productTierKey(tier)}>
                  {tier.categoryName} · {tier.tierName}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={selectedStatus}
              onChange={(e) => onStatusChange(e.target.value as Exclude<SubscriptionStatus, 'Cancelled'>)}
              sx={{ minWidth: 140 }}
            >
              {COMMERCIAL_OVERRIDE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {SUBSCRIPTION_STATUS_LABEL[status]}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              color="warning"
              disabled={!selectedCatalogTier || isSubmitting}
              onClick={onSubmit}
            >
              {isSubmitting ? <CircularProgress size={18} /> : 'Aplicar ajuste'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

function CommercialProductChip({ product }: { product: OrganizationCommercialProduct }): JSX.Element {
  return (
    <Chip
      variant="outlined"
      color="primary"
      label={commercialProductChipLabel(product)}
    />
  );
}

export function commercialProductChipLabel(product: Pick<OrganizationCommercialProduct, 'productName' | 'tierName' | 'status'>): string {
  const status = SUBSCRIPTION_STATUS_LABEL[product.status as SubscriptionStatus] ?? product.status;
  return `${product.productName} · ${product.tierName} · ${status}`;
}

export function commercialDiscountSummary(coupon: Pick<adminApi.CouponApplication, 'code' | 'percentOff'>): string {
  return `${coupon.code} · ${coupon.percentOff}% off`;
}

export function isAutomaticallyApplicableCoupon(coupon: Pick<adminApi.AdminCoupon, 'percentOff'>): boolean {
  return coupon.percentOff === 100;
}

export function productTierKey(tier: Pick<ProductCatalogEntry, 'product' | 'tier'>): string {
  return `${tier.product}:${tier.tier}`;
}

function Field({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>
        {value}
      </Typography>
    </Grid>
  );
}
